import i18n from '@/i18n'
import type { LandingLocale, LocalizedSlugs } from '@/navigation/localeConstants'

export type LearnArticleDefinition = {
    id: string
    slugs: LocalizedSlugs
    title: string
    description: string
    category: string
    authorName: string
    readingMinutes: number
    publishedAt: string
    dateModified: string
    imagePath: string
}

/** Localized view model used by article UI and Helmet. */
export type LearnArticleMeta = {
    id: string
    slug: string
    title: string
    description: string
    category: string
    authorName: string
    readingMinutes: number
    publishedAt: string
    dateModified: string
    imagePath: string
}

export type LearnArticleBodyProps = {
    article: LearnArticleMeta
}

export function learnArticleI18nKey(id: string, field: string) {
    return `learn.articles.${id}.${field}`
}

export const LEARN_ARTICLES: LearnArticleDefinition[] = [
    {
        id: 'menu-navidad-grupos-escandallo',
        slugs: {
            es: 'menu-navidad-grupos-escandallo',
            en: 'christmas-group-menu-recipe-costing',
        },
        title: 'Menú de Navidad para grupos: escandalla el que más vendes antes de que diciembre se coma el margen',
        description:
            'El menú de Navidad para grupos se cobra a precio de octubre y se paga en diciembre: la cesta navideña subió un 10,3 % en 2025. Escandalla el que más vendes.',
        category: 'Escandallos',
        authorName: 'Samuel Carrillo',
        readingMinutes: 10,
        publishedAt: '2026-10-28',
        dateModified: '2026-10-28',
        imagePath: '/aprender/menu-navidad-grupos-escandallo/hero.webp',
    },
    {
        id: 'que-es-el-prime-cost',
        slugs: {
            es: 'que-es-el-prime-cost',
            en: 'what-is-prime-cost',
        },
        title: 'Prime cost del restaurante: el famoso 70 % no es la barrera, es el borde del precipicio',
        description:
            'Prime cost del restaurante: el 70 % no es la meta, es el borde. En España el personal ya pesa un 31 % y sin tu sueldo el número engaña. Guía, octubre 2026.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 11,
        publishedAt: '2026-10-27',
        dateModified: '2026-10-27',
        imagePath: '/aprender/que-es-el-prime-cost/hero.webp',
    },
    {
        id: 'inventario-teorico-real-no-cuadra',
        slugs: {
            es: 'inventario-teorico-real-no-cuadra',
            en: 'theoretical-vs-actual-inventory-dont-match',
        },
        title: 'Inventario teórico y real: por qué no cuadran y en qué orden buscar la causa',
        description:
            'Inventario teórico y real: un 3-5 % de desviación es normal. Compara en kilos y en euros y busca en oficina, recepción, cocina y sala. Caso: 2.600 €/año.',
        category: 'Inventario',
        authorName: 'Samuel Carrillo',
        readingMinutes: 10,
        publishedAt: '2026-10-23',
        dateModified: '2026-10-23',
        imagePath: '/aprender/inventario-teorico-real-no-cuadra/hero.webp',
    },
    {
        id: 'mermas-hosteleria-prueba-papelera',
        slugs: {
            es: 'mermas-hosteleria-prueba-papelera',
            en: 'hospitality-waste-bin-test',
        },
        title: 'Mermas en hostelería: una semana apuntando lo que tiras te dice cuánto pierdes (y por qué no siempre es robo)',
        description:
            'Las mermas en hostelería son tres cosas y solo una puede ser robo. Una semana apuntando lo que tiras: en nuestro ejemplo, 135 € y dos puntos de food cost.',
        category: 'Inventario',
        authorName: 'Samuel Carrillo',
        readingMinutes: 9,
        publishedAt: '2026-10-15',
        dateModified: '2026-10-15',
        imagePath: '/aprender/mermas-hosteleria-prueba-papelera/hero.webp',
    },
    {
        id: 'como-hacer-inventario-hosteleria',
        slugs: {
            es: 'como-hacer-inventario-hosteleria',
            en: 'how-to-do-inventory-in-hospitality',
        },
        title: 'Cómo hacer un inventario en hostelería: cuenta 25 productos, no 250, y encuentra la merma que no ves',
        description:
            'Cómo hacer un inventario en hostelería contando solo 20 o 30 productos: el último día del mes, sin IVA y al último albarán. La diferencia es tu merma.',
        category: 'Inventario',
        authorName: 'Samuel Carrillo',
        readingMinutes: 9,
        publishedAt: '2026-10-13',
        dateModified: '2026-10-13',
        imagePath: '/aprender/como-hacer-inventario-hosteleria/hero.webp',
    },
    {
        id: 'como-saber-si-mi-restaurante-es-rentable',
        slugs: {
            es: 'como-saber-si-mi-restaurante-es-rentable',
            en: 'how-to-know-if-my-restaurant-is-profitable',
        },
        title: 'Cómo saber si tu restaurante es rentable cuando llevas los números en la cabeza',
        description:
            'Un consultor estima que el 90% de los hosteleros no tiene cuenta de resultados. Cómo saber si tu restaurante es rentable y en qué nivel de control estás.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 8,
        publishedAt: '2026-09-18',
        dateModified: '2026-09-18',
        imagePath: '/aprender/como-saber-si-mi-restaurante-es-rentable/hero.webp',
    },
    {
        id: 'que-es-un-escandallo',
        slugs: {
            es: 'que-es-un-escandallo',
            en: 'what-is-a-recipe-costing',
        },
        title: 'Qué es un escandallo (y por qué el de tu carta probablemente está caducado)',
        description:
            'Qué es un escandallo: el coste real de cada ración. Un pulpo que crees a 3,32 € cuesta 5,78 €. Qué incluye, qué no es y por qué caduca con cada albarán.',
        category: 'Escandallos',
        authorName: 'Javier Porras',
        readingMinutes: 7,
        publishedAt: '2026-09-15',
        dateModified: '2026-09-15',
        imagePath: '/aprender/coste-por-plato/merma.webp',
    },
    {
        id: 'beneficio-acumulado-cuanto-sacar-restaurante',
        slugs: {
            es: 'beneficio-acumulado-cuanto-sacar-restaurante',
            en: 'cumulative-profit-how-much-you-can-take',
        },
        title: 'El mes bueno no decide cuánto puedes sacar: decide el acumulado',
        description:
            'Marzo puede dar 4.750 € de resultado y 2.900 € «tuyos». El trimestre cierra con 750 € de beneficio y 2.100 € menos en la cuenta. Por qué manda el acumulado.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 6,
        publishedAt: '2026-09-13',
        dateModified: '2026-09-13',
        imagePath: '/aprender/beneficio-acumulado-cuanto-sacar-restaurante/hero.webp',
    },
    {
        id: 'plan-viabilidad-hosteleria-primeros-pasos',
        slugs: {
            es: 'plan-viabilidad-hosteleria-primeros-pasos',
            en: 'hospitality-viability-plan-first-steps',
        },
        title: 'Los primeros pasos de tu plan de viabilidad (y el error de empezar por el local)',
        description:
            'Los primeros pasos para montar el plan de viabilidad de tu negocio de hostelería: concepto, mercado, costes y punto de equilibrio, con fórmula y ejemplo.',
        category: 'Emprender en hostelería',
        authorName: 'Samuel Carrillo',
        readingMinutes: 8,
        publishedAt: '2026-09-08',
        dateModified: '2026-09-08',
        imagePath: '/aprender/plan-viabilidad-hosteleria-primeros-pasos/hero.webp',
    },
    {
        id: 'beneficio-real-restaurante-cuanto-puedo-sacar',
        slugs: {
            es: 'beneficio-real-restaurante-cuanto-puedo-sacar',
            en: 'real-restaurant-profit-how-much-can-you-take',
        },
        title: 'Cómo calcular el beneficio real de tu restaurante (y cuánto de ese dinero puedes sacar)',
        description:
            'Tu bar ganó 4.750 € en marzo y solo puedes sacar 2.900 €. Cómo calcular el beneficio real de un restaurante y saber cuánto de ese dinero es tuyo de verdad.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 7,
        publishedAt: '2026-09-08',
        dateModified: '2026-09-13',
        imagePath: '/aprender/beneficio-real-restaurante-cuanto-puedo-sacar/hero.webp',
    },
    {
        id: 'de-idea-a-negocio-paso-1-viabilidad',
        slugs: {
            es: 'de-idea-a-negocio-paso-1-viabilidad',
            en: 'from-idea-to-business-step-1-viability',
        },
        title: 'Antes de montar un negocio de hostelería, hazte esta pregunta (que no es cuánto dinero tienes)',
        description:
            'Antes de montar un negocio de hostelería, comprueba si tienes los cuatro medios que necesitas: dinero, oficio, mercado y ubicación. Primer paso de la guía.',
        category: 'Emprender en hostelería',
        authorName: 'Samuel Carrillo',
        readingMinutes: 6,
        publishedAt: '2026-08-25',
        dateModified: '2026-09-13',
        imagePath: '/aprender/de-idea-a-negocio-paso-1-viabilidad/hero.webp',
    },
    {
        id: 'escandallo-menu-del-dia',
        slugs: {
            es: 'escandallo-menu-del-dia',
            en: 'daily-menu-recipe-costing',
        },
        title: 'Cómo hacer el escandallo de un menú del día',
        description:
            'Un menú del día de 16 € solo deja 4,30 € para primero, segundo y postre. Te contamos cómo hacer bien el escandallo y qué platos puedes meter en la pizarra.',
        category: 'Escandallos',
        authorName: 'Samuel Carrillo',
        readingMinutes: 8,
        publishedAt: '2026-08-20',
        dateModified: '2026-09-13',
        imagePath: '/aprender/escandallo-menu-del-dia/hero.webp',
    },
    {
        id: 'controlar-numeros-restaurante-no-delegar',
        slugs: {
            es: 'controlar-numeros-restaurante-no-delegar',
            en: 'control-restaurant-numbers-dont-delegate',
        },
        title: 'Números de restaurantes: por qué no puedes delegar el control del todo',
        description:
            'Cómo leer los números de un restaurante sin delegarlos del todo. Solo el 39% revisa rentabilidad cada semana y 6 de cada 10 no llegan a 5 años.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 8,
        publishedAt: '2026-08-17',
        dateModified: '2026-09-15',
        imagePath: '/aprender/controlar-numeros-restaurante-no-delegar/hero.webp',
    },
    {
        id: 'cada-cuanto-revisar-numeros-restaurante',
        slugs: {
            es: 'cada-cuanto-revisar-numeros-restaurante',
            en: 'how-often-to-review-restaurant-numbers',
        },
        title: '¿Cada cuánto hay que mirar los números del restaurante?',
        description:
            'Cada cuánto revisar los números de tu restaurante para no descubrir tarde que dos meses flojos te revientan el año. Cadencia, ejemplo con cifras y plan semanal.',
        category: 'Beneficio',
        authorName: 'Samuel Carrillo',
        readingMinutes: 8,
        publishedAt: '2026-08-01',
        dateModified: '2026-09-13',
        imagePath: '/aprender/cada-cuanto-revisar-numeros-restaurante/hero.webp',
    },
    {
        id: 'control-albaranes-hosteleria',
        slugs: {
            es: 'control-albaranes-hosteleria',
            en: 'delivery-note-control-hospitality',
        },
        title: 'Control y gestión de albaranes en hostelería: cómo proteger el margen',
        description:
            'Guía de control y gestión de albaranes en hostelería: revisar cada entrega frente al pedido para no pagar de más y proteger el margen del restaurante.',
        category: 'Inventario',
        authorName: 'Samuel Carrillo',
        readingMinutes: 7,
        publishedAt: '2026-08-02',
        dateModified: '2026-09-15',
        imagePath: '/aprender/control-albaranes-hosteleria/hero.webp',
    },
    {
        id: 'que-es-el-food-cost',
        slugs: {
            es: 'que-es-el-food-cost',
            en: 'what-is-food-cost',
        },
        title: 'Qué es el food cost (y por qué el tuyo no es un solo número)',
        description:
            'El food cost es el coste de ingredientes entre el precio sin IVA. Pero hay uno teórico, uno real, uno por plato y otro de negocio. Aquí la diferencia.',
        category: 'Beneficio',
        authorName: 'Javier Porras',
        readingMinutes: 4,
        publishedAt: '2026-09-15',
        dateModified: '2026-09-15',
        imagePath: '/aprender/que-es-el-food-cost/hero.webp',
    },
    {
        id: 'que-food-cost-deberia-tener-restaurante-espana',
        slugs: {
            es: 'que-food-cost-deberia-tener-restaurante-espana',
            en: 'what-food-cost-should-a-restaurant-have-in-spain',
        },
        title: '¿Qué food cost debería tener un restaurante en España?',
        description:
            'No existe un porcentaje perfecto para todos. Referencias del 25 % al 35 %, prime cost, inventario y cómo saber qué coste de producto soporta tu modelo.',
        category: 'Beneficio',
        authorName: 'Javier Porras',
        readingMinutes: 7,
        publishedAt: '2026-05-13',
        dateModified: '2026-10-23',
        imagePath: '/aprender/que-food-cost-deberia-tener-restaurante-espana/hero.webp',
    },
    {
        id: 'como-calcular-coste-de-un-plato',
        slugs: {
            es: 'como-calcular-coste-de-un-plato',
            en: 'how-to-calculate-dish-cost',
        },
        title: 'Cómo calcular cuánto cuesta cada plato de tu restaurante y dejar de perder dinero sin darte cuenta',
        description:
            'Puedes tener el comedor lleno y seguir ganando menos de lo que crees. Escandallo, food cost y margen explicados para hostelería.',
        category: 'Escandallos',
        authorName: 'Javier Porras',
        readingMinutes: 6,
        publishedAt: '2026-05-27',
        dateModified: '2026-09-15',
        imagePath: '/aprender/coste-por-plato/hero-facturas.webp',
    },
    {
        id: 'como-hacer-un-escandallo-paso-a-paso',
        slugs: {
            es: 'como-hacer-un-escandallo-paso-a-paso',
            en: 'how-to-make-a-recipe-costing-step-by-step',
        },
        title: 'Cómo hacer un escandallo: guía paso a paso (con ejemplo de hamburguesa)',
        description:
            'Cómo hacer un escandallo en seis pasos: báscula, merma y precios al día. Ejemplo de hamburguesa para sacar el coste real por ración.',
        category: 'Escandallos',
        authorName: 'Javier Porras',
        readingMinutes: 7,
        publishedAt: '2026-06-10',
        dateModified: '2026-09-15',
        imagePath: '/aprender/coste-por-plato/bascula.webp',
    },
    {
        id: 'food-cost-y-margen-bruto-restaurante',
        slugs: {
            es: 'food-cost-y-margen-bruto-restaurante',
            en: 'food-cost-and-gross-margin-restaurant',
        },
        title: 'Food cost y margen bruto: cómo saber cuánto ganas realmente con un plato',
        description: 'Calcula el food cost y el margen en euros sin IVA, y deja de convertir el 28 % en una religión.',
        category: 'Beneficio',
        authorName: 'Javier Porras',
        readingMinutes: 6,
        publishedAt: '2026-06-24',
        dateModified: '2026-09-13',
        imagePath: '/aprender/food-cost-y-margen-bruto-restaurante/hero.webp',
    },
    {
        id: 'coste-teorico-vs-coste-real-cocina',
        slugs: {
            es: 'coste-teorico-vs-coste-real-cocina',
            en: 'theoretical-vs-actual-kitchen-cost',
        },
        title: 'Coste teórico vs coste real (costo teórico): por qué el escandallo puede mentir',
        description:
            'Qué es el coste teórico (o costo teórico) frente al coste real en cocina, cómo compararlos y qué errores hacen mentir al escandallo.',
        category: 'Inventario',
        authorName: 'Javier Porras',
        readingMinutes: 6,
        publishedAt: '2026-07-08',
        dateModified: '2026-09-15',
        imagePath: '/aprender/coste-teorico-vs-coste-real-cocina/hero.webp',
    },
    {
        id: 'disenar-carta-rentable-restaurante',
        slugs: {
            es: 'disenar-carta-rentable-restaurante',
            en: 'design-a-more-profitable-menu',
        },
        title: 'Cómo diseñar una carta más rentable',
        description:
            'Empieza por donde más dinero se mueve, clasifica tus platos y convierte el control de costes en una rutina.',
        category: 'Beneficio',
        authorName: 'Javier Porras',
        readingMinutes: 6,
        publishedAt: '2026-07-22',
        dateModified: '2026-09-13',
        imagePath: '/aprender/disenar-carta-rentable-restaurante/hero.webp',
    },
]

export function localizeLearnArticle(article: LearnArticleDefinition, locale: LandingLocale): LearnArticleMeta {
    const t = i18n.getFixedT(locale)
    return {
        id: article.id,
        slug: article.slugs[locale],
        title: String(t(learnArticleI18nKey(article.id, 'title'), article.title)),
        description: String(t(learnArticleI18nKey(article.id, 'description'), article.description)),
        category: String(t(learnArticleI18nKey(article.id, 'category'), article.category)),
        authorName: String(t(learnArticleI18nKey(article.id, 'authorName'), article.authorName)),
        readingMinutes: article.readingMinutes,
        publishedAt: article.publishedAt,
        dateModified: article.dateModified,
        imagePath: article.imagePath,
    }
}

export function getLearnArticleById(id: string) {
    return LEARN_ARTICLES.find(a => a.id === id)
}

export function getLearnArticleByLocaleSlug(locale: LandingLocale, slug: string) {
    return LEARN_ARTICLES.find(a => a.slugs[locale] === slug)
}

/** @deprecated Prefer getLearnArticleByLocaleSlug — matches ES slug or id for compatibility. */
export function getLearnArticle(slug: string) {
    return (
        getLearnArticleByLocaleSlug('es', slug) ?? getLearnArticleByLocaleSlug('en', slug) ?? getLearnArticleById(slug)
    )
}

export function learnArticlePath(articleOrId: LearnArticleDefinition | string) {
    const article =
        typeof articleOrId === 'string'
            ? (getLearnArticleById(articleOrId) ?? getLearnArticle(articleOrId))
            : articleOrId
    if (!article) return '/aprender'
    return `/aprender/${article.slugs.es}`
}

/** Locale-free ES path for LocaleSeoLinks / SSG identity. */
export function learnArticleLocaleFreePath(article: LearnArticleDefinition) {
    return `/aprender/${article.slugs.es}`
}
